import { Row, Col, Alert } from 'react-bootstrap';
import { MdInfoOutline, MdEmail, MdEdit, MdOutlineDeleteOutline } from 'react-icons/md';
import { FaSlack } from 'react-icons/fa';
import { BPMTable } from '@Components/StickyTable/BPMTable';
import { ToggleSwitch } from '@Components/ToggleSwitch/ToggleSwitch';
import { Skeleton, TableSkeleton } from '@Components/Skeleton/Skeleton';
import { IconButton } from '@Components/IconButton';
import { Size } from '@Components/designConstants';
import { GroupedAlert, formatTimeAgo } from './utils/alertUtils';
import './AnomalyDetection.scss';

interface AlertsTabProps {
  loading: boolean;
  groupedAlerts: GroupedAlert[];
  onToggleActive: (alertGroupId: string) => void;
  onEditAlert: (alertGroupId: string) => void;
  onDeleteAlert: (alertGroupId: string) => void;
}

const NoAlertsRow = () => (
  <div className='no-alerts' data-testid='no-alerts'>
    No alerts configured
  </div>
);

export const AlertsTab = ({
  loading,
  groupedAlerts,
  onToggleActive,
  onEditAlert,
  onDeleteAlert
}: AlertsTabProps) => {
  const hasEmailNotifications = (groupedAlert: GroupedAlert): boolean =>
    groupedAlert.alertChannels.some(channel => channel.type === 'email');

  const hasSlackNotifications = (groupedAlert: GroupedAlert): boolean =>
    groupedAlert.alertChannels.some(channel => channel.type === 'slack');

  const tableHeaders = [
    {
      name: 'alertName',
      label: 'Alert Name',
      flex: 2,
      renderer: (data: GroupedAlert) => (
        <div>
          <div className='alert-name' data-testid='alert-name'>
            {data.alertName}
          </div>
        </div>
      )
    },
    {
      name: 'notifications',
      label: 'Notifications',
      flex: 1,
      renderer: (data: GroupedAlert) => (
        <div className='notification-icons'>
          {hasEmailNotifications(data) && (
            <MdEmail className='notification-icon email' title='Email notifications enabled' />
          )}
          {hasSlackNotifications(data) && (
            <FaSlack className='slack-icon' title='Slack notifications enabled' />
          )}
        </div>
      )
    },
    {
      name: 'groupIsActive',
      label: 'Active',
      flex: 1,
      renderer: (data: GroupedAlert) => (
        <div data-testid={`alert-toggle-${data.alertGroupId}`}>
          <ToggleSwitch
            label=''
            checked={data.groupIsActive}
            onChange={() => onToggleActive(data.alertGroupId)}
            size={Size.SM}
          />
        </div>
      )
    },
    {
      name: 'lastModified',
      label: 'Last Modified',
      flex: 1,
      renderer: (data: GroupedAlert) => (
        <span className='last-modified'>{formatTimeAgo(data.lastModified)}</span>
      )
    },
    {
      name: 'lastModifiedBy',
      label: 'Last Modified By',
      flex: 1,
      renderer: (data: GroupedAlert) => (
        <span className='last-modified-by'>
          {data.lastModifiedByEmail || data.createdByEmail || 'Unknown'}
        </span>
      )
    },
    {
      name: 'actions',
      label: 'Actions',
      flex: 1,
      renderer: (data: GroupedAlert) => (
        <div className='action-buttons'>
          <IconButton
            size={Size.SM}
            onClick={() => onEditAlert(data.alertGroupId)}
            className='edit-btn'
            data-testid={`edit-alert-${data.alertGroupId}`}
          >
            <MdEdit />
          </IconButton>
          <IconButton
            size={Size.SM}
            onClick={() => onDeleteAlert(data.alertGroupId)}
            className='delete-btn'
            data-testid={`delete-alert-${data.alertGroupId}`}
          >
            <MdOutlineDeleteOutline />
          </IconButton>
        </div>
      )
    }
  ];

  return (
    <div className='anomaly-detection-content'>
      <Row>
        <Col>
          <Alert variant='info' className='info-alert'>
            <MdInfoOutline className='alert-icon' />
            <div>
              Welcome to <strong>Alerts</strong>, manage your alerts and notification preferences
              below.
            </div>
          </Alert>
        </Col>
      </Row>

      <Row>
        <Col>
          <div className='alerts-table-container' data-testid='alerts-table'>
            {loading ? (
              <Skeleton>
                <TableSkeleton />
              </Skeleton>
            ) : (
              <BPMTable headers={tableHeaders} data={groupedAlerts} noRowsRenderer={NoAlertsRow} />
            )}
          </div>
        </Col>
      </Row>
    </div>
  );
};
